// This code is a React component for a Sign In page. It uses the `react-hook-form` library for form handling and validation, and it integrates with a Redux store to manage user authentication. The page includes fields for email and password, a "Forgot Password" link, and a login button. It also displays notifications for errors during the login process.
// The left side of the page features a background with a logo and a motivational message, while the right side contains the login form. The component handles form submission, validation, and error handling, providing a user-friendly interface for signing in.

import { useEffect, useRef, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  ButtonWizard,
  TextInput,
  LinkWizard,
  LinkWizardSo,
  TurnstileCaptcha,
  TurnstileCaptchaRef,
} from '../../components';
import { useGetAuthConfigurationQuery, useLoginMutation } from '../../store';
import { useAuth, useNotification } from '../../contexts';
import { Link, useNavigate } from 'react-router-dom';
import { ErrorCode } from '../../enums';
import { EMAIL_REGEX, NUMBER_REGEX } from '../../constants/regexValidations';
import { extractErrorMessageFromAPI } from '../../utils';
import globeImage from '../../assets/images/globe1.png';
import { encryptPassword } from '../../utils/encryption';
import PublicAuthLayout from './PublicAuthLayout';
import softgelLogoImg from '../../assets/images/softgel_logo.png';
import softgelBgImg from '../../assets/images/softgel_bg.png';
import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';
import { saveAssetToken } from '../../services/knowledgeHub';
import config from '../../config';


export interface IUserSigninProps {
  email: string;
  password: string;
}

export default function SignIn() {
  const { showNotification } = useNotification();
  const [loginUser, { isLoading }] = useLoginMutation();
  const {
    data: authConfiguration,
    isError: isAuthConfigurationError,
    isLoading: isAuthConfigurationLoading,
    refetch: refetchAuthConfiguration,
  } = useGetAuthConfigurationQuery();
  const navigate = useNavigate();
  const {
    setIsAuthenticated,
    setUser,
    setSessionLimitData,
    setAuthenticationMethod,
  } = useAuth();
  const ssoEnabled = authConfiguration?.data?.ssoEnabled;
  const captchaEnabled = Boolean(authConfiguration?.data?.captchaEnabled);
  const legacyEnabled = ssoEnabled === false;
  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const handleCaptchaVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  const handleCaptchaExpire = useCallback(() => {
    setCaptchaToken(null);
  }, []);

  const handleCaptchaError = useCallback(() => {
    setCaptchaToken(null);
  }, []);

  useEffect(() => {
    const errorCode = new URLSearchParams(window.location.search).get('authError');
    if (!errorCode) return;
    const messages: Record<string, string> = {
      ACCOUNT_NOT_REGISTERED:
        'Your SSO account is not registered in CConnect. Contact an administrator.',
      ACCOUNT_INACTIVE: 'Your CConnect account is inactive.',
      ACCOUNT_ALREADY_LINKED:
        'Your CConnect account is already linked to another SSO identity. Contact an administrator.',
      IDENTITY_CONFLICT:
        'Your SSO identity conflicts with an existing account link. Contact an administrator.',
      IDENTITY_DISABLED:
        'Your SSO identity link is disabled. Contact an administrator.',
      AMBIGUOUS_LOCAL_ACCOUNT:
        'Multiple CConnect accounts match this SSO identity. Contact an administrator.',
      EMAIL_NOT_VERIFIED: 'Your SSO email address is not verified.',
      SSO_CANCELLED: 'SSO sign-in was cancelled.',
      SSO_TRANSACTION_EXPIRED: 'The SSO request expired. Please try again.',
      INVALID_SSO_STATE: 'The SSO request could not be verified. Please try again.',
      SSO_FAILED: 'SSO sign-in failed. Please try again.',
    };
    showNotification(
      'error',
      ErrorCode.SIGN_IN_ERROR,
      messages[errorCode] || messages.SSO_FAILED,
    );
    window.history.replaceState({}, document.title, '/signin');
  }, [showNotification]);

  function handleSsoLogin() {
    const apiBaseUrl = import.meta.env.VITE_ENV_HOST_URL || config.urls.host;
    const returnTo = localStorage.getItem('redirectPath') || '/';
    const loginUrl = new URL('/auth/sso/login', apiBaseUrl);
    loginUrl.searchParams.set('returnTo', returnTo);
    loginUrl.searchParams.set('frontendOrigin', window.location.origin);
    window.location.assign(loginUrl.href);
  }

  const {
    control,
    formState,
    handleSubmit,
    clearErrors,
    setError,
    setFocus,
  } = useForm<IUserSigninProps>({
    defaultValues: {
      password: '',
      email: '',
    },
  });

  async function handleSubmitForm(data: IUserSigninProps) {
    clearErrors('password');

    if (!data.password) {
      showNotification('error', ErrorCode.SIGN_IN_ERROR, 'Password is required');
      return;
    }

    if (captchaEnabled && !captchaToken) {
      showNotification(
        'error',
        ErrorCode.SIGN_IN_ERROR,
        'Please Complete Security Verification',
      );
      return;
    }

    const parsedData = {
      email: data.email?.trim()?.toLowerCase(),
      password: encryptPassword(data.password),
      ...(captchaToken ? { captchaToken } : {}),
    };
    try {
      const response = await loginUser(parsedData).unwrap();
      sessionStorage.removeItem('redirected');
      setIsAuthenticated(true);
      setUser(response?.data?.user);
      saveAssetToken(response?.data?.assetToken);
      setAuthenticationMethod('LEGACY_PASSWORD');
      navigate('/');
    } catch (err: any) {
      captchaRef.current?.reset();
      setCaptchaToken(null);

      // Handle device limit reached
      const errorData = err?.data;
      if (errorData?.errorCode === 'SESSION_LIMIT_REACHED') {
        setSessionLimitData({
          activeSessions: errorData.data.activeSessions,
          maxSessions: errorData.data.maxSessions,
          email: parsedData.email,
          password: parsedData.password,
        });
        navigate('/device-limit');
        return;
      }

      const errorMessage =
        extractErrorMessageFromAPI(err) ||
        'Error in logging in user. Please try again later';

      if (errorMessage === 'Invalid credentials') {
        setError('password', {
          type: 'server',
          message: errorMessage,
        });
        setFocus('password');
      }

      showNotification(
        'error',
        ErrorCode.SIGN_IN_ERROR,
        errorMessage,
      );
    }
  }

  const clientCode = import.meta.env.VITE_ENV_CLIENT_CODE;
  const isSoftgel = clientCode === 'SOFTGEL';
  const logoSrc = isSoftgel
    ? softgelLogoImg
    : clientCode === 'HATHWAY'
      ? hathLogo
      : denLogo;

  // Compact UI sizes for all clients (less height, no scroll)
  const headerMbClass = 'mb-6';
  const headerTitleClass = 'text-2xl mb-1';
  const headerSubtitleClass = 'text-xs';
  const cardPaddingClass = 'p-6';
  const formSpaceYClass = 'space-y-3';
  const labelClass = 'text-xs font-semibold text-gray-700';
  const inputClass = 'h-10 text-sm';
  const primaryBtnClass = 'h-10 text-sm';
  const dividerPyClass = 'py-2';
  const otpBtnClass = 'h-10 gap-2 text-sm';
  const footerMtClass = 'mt-4';

  const formContent = (
    <>
      {/* Header */}
      <div className={headerMbClass}>
        <h2 className={`${headerTitleClass} font-bold text-gray-900`}>
          Welcome back
        </h2>
        <p className={`${headerSubtitleClass} text-gray-600`}>
          Enter your credentials to access your account
        </p>
      </div>

      {/* Form Card */}
      <div className={`bg-white rounded-2xl shadow-lg border border-gray-200 ${cardPaddingClass}`}>
        <form
          className={formSpaceYClass}
          onSubmit={handleSubmit(handleSubmitForm)}
        >
          {isAuthConfigurationLoading && (
            <p className="text-center text-sm text-gray-600">
              Loading sign-in configuration…
            </p>
          )}

          {isAuthConfigurationError && (
            <div className="space-y-3 text-center">
              <p className="text-sm text-red-600">
                Sign-in configuration is temporarily unavailable.
              </p>
              <button
                type="button"
                onClick={() => refetchAuthConfiguration()}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                Retry
              </button>
            </div>
          )}

          {legacyEnabled && <TextInput
            name={'email'} 
            control={control}
            rules={{
              required: 'Email or Employee Code is required',
              validate: (value) => {
                const trimmedValue = value.trim();
                
                const isEmail = EMAIL_REGEX.test(trimmedValue);
              
                const isNumericEmpCode = NUMBER_REGEX.test(trimmedValue);
                if (!isEmail && !isNumericEmpCode) {
                  return 'Please enter a valid email or employee code';
                }
                
                return true;
              }
            }}
            label={'Email or Employee Code'}
            labelClassName={labelClass}
            formState={formState}
            config={{
              placeholder: isSoftgel ? 'name@company.com or 12345' : 'you@company.com or 12345',
              className: inputClass,
            }}
          />}

          {legacyEnabled && <TextInput
            name={'password'}
            control={control}
            label={'Password'}
            labelClassName={labelClass}
            variant="password"
            formState={formState}
            rules={{ required: 'Password is required' }}
            config={{
              placeholder: 'Enter your password',
              className: inputClass,
            }}
          />}

          {legacyEnabled && <div className="flex items-center justify-end -mt-1">
            {isSoftgel ? (
              <LinkWizardSo href="/forgot-password" addOnClass="no-underline">
                Forgot password?
              </LinkWizardSo>
            ) : (
              <LinkWizard href="/forgot-password" addOnClass="no-underline text-blue-600 hover:text-blue-700">
                Forgot password?
              </LinkWizard>
            )}
          </div>}

          {legacyEnabled && captchaEnabled && (
            <TurnstileCaptcha
              ref={captchaRef}
              action="login"
              enabled={captchaEnabled}
              onVerify={handleCaptchaVerify}
              onExpire={handleCaptchaExpire}
              onError={handleCaptchaError}
              className="my-1"
            />
          )}

          {legacyEnabled && <ButtonWizard
            buttonType="submit"
            addOnClass={`w-full ${primaryBtnClass} text-white rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg ${isSoftgel ? 'bg-[#9d0b2d] hover:bg-[#7f0823]' : 'bg-blue-600 hover:bg-blue-700'} mt-1`}
            loading={isLoading}
          >
            Sign in
          </ButtonWizard>}

          {/* Divider */}
          {legacyEnabled && <div className={`relative ${dividerPyClass}`}>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <span className={`px-4 bg-white ${isSoftgel ? 'text-xs' : 'text-sm'} text-gray-500 font-medium`}>
                {isSoftgel ? 'OR' : 'Or'}
              </span>
            </div>
          </div>}

          {/* OTP Login Button */}
          {legacyEnabled && <Link
            to="/login-with-otp"
            className={`w-full flex items-center justify-center px-4 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 ${otpBtnClass}`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            Sign in with OTP
          </Link>}

          {ssoEnabled && (
            <button
              type="button"
              onClick={handleSsoLogin}
              className={`w-full flex items-center justify-center px-4 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 ${otpBtnClass}`}
            >
              Sign in with Sapphire SSO
            </button>
          )}
        </form>
      </div>

      {/* Footer */}
      <div className={`${footerMtClass} space-y-1 text-center`}>
        <p className="text-[11px] text-gray-500">
          &copy; {new Date().getFullYear()}{' '}
          <Link
            target="_blank"
            to="https://sapphiretech.co.in"
            className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
            rel="noopener noreferrer"
          >
            Sapphire Technocrats Pvt Ltd.
          </Link>{' '}
          &mdash; All rights reserved
        </p>
      </div>
    </>
  );

  if (isSoftgel) {
    // Softgel ke liye naya template + images
    return (
      <div className="h-screen overflow-hidden">
        <PublicAuthLayout
          logoSrc={logoSrc}
          leftBgImageSrc={softgelBgImg}
          accentTextClass="text-teal-500"
        >
          {formContent}
        </PublicAuthLayout>
      </div>
    );
  }

  // Baaki clients ke liye purana design hi rahe
  return (
    <div className="h-screen overflow-hidden flex font-sans bg-gray-50">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-onbording0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Globe Image */}
        <img
          src={globeImage}
          alt="Globe"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-auto opacity-20 pointer-events-none"
          loading="eager"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-8 text-white h-full">
          <div className="space-y-6 max-w-lg">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={logoSrc}
                alt="Client logo"
                className="w-20 h-auto"
                loading="eager"
              />
            </div>

            {/* Title */}
            <div className="space-y-3">
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight">
                Enterprise Portal
              </h1>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full" />
              <p className="text-lg text-blue-100 leading-relaxed">
                Secure access to your dashboard
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="space-y-1">
                <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold">Secure</h3>
                <p className="text-xs text-blue-200 leading-relaxed">
                  Enterprise-grade encryption
                </p>
              </div>

              <div className="space-y-1">
                <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold">Analytics</h3>
                <p className="text-xs text-blue-200 leading-relaxed">
                  Real-time insights
                </p>
              </div>

              <div className="space-y-1">
                <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold">24/7 Access</h3>
                <p className="text-xs text-blue-200 leading-relaxed">
                  Available anytime
                </p>
              </div>

              <div className="space-y-1">
                <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold">Team Ready</h3>
                <p className="text-xs text-blue-200 leading-relaxed">
                  Multi-user support
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-6">
        <div className="w-full max-w-md">{formContent}</div>
      </div>
    </div>
  );
}

  // return (
  //   <div className="min-h-screen flex font-sans bg-gray-50">
  //     {/* Left Side - Brand Panel */}
  //     <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
  //       {/* Animated Background Pattern */}
  //       <div className="absolute inset-0 opacity-10">
  //         <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
  //         <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
  //       </div>

  //       {/* Globe Image */}
  //       <img
  //         src={globeImage}
  //         alt="Globe"
  //         className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-auto opacity-20 pointer-events-none"
  //         loading="eager"
  //       />

  //       {/* Content */}
  //       <div className="relative z-10 flex flex-col justify-between px-16 py-20 text-white h-full">
  //         {/* Top Section */}
  //         <div className="space-y-8">
  //           <div className="space-y-6">
  //             <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20">
  //               <svg
  //                 className="w-9 h-9"
  //                 fill="currentColor"
  //                 viewBox="0 0 24 24"
  //               >
  //                 <path d="M13 10V3L4 14h7v7l9-11h-7z" />
  //               </svg>
  //             </div>

  //             <div className="space-y-4 max-w-lg">
  //               <h1 className="text-6xl font-bold leading-[1.1] tracking-tight">
  //                 Enterprise Portal
  //               </h1>
  //               <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
  //               <p className="text-xl text-blue-100 leading-relaxed">
  //                 Secure access to your dashboard
  //               </p>
  //             </div>
  //           </div>
  //         </div>

  //         {/* Bottom Section - Features */}
  //         <div className="space-y-6 max-w-lg">
  //           <div className="grid grid-cols-2 gap-6">
  //             <div className="space-y-2">
  //               <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
  //                 <svg
  //                   className="w-6 h-6"
  //                   fill="currentColor"
  //                   viewBox="0 0 20 20"
  //                 >
  //                   <path
  //                     fillRule="evenodd"
  //                     d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
  //                     clipRule="evenodd"
  //                   />
  //                 </svg>
  //               </div>
  //               <h3 className="text-base font-semibold">Secure</h3>
  //               <p className="text-sm text-blue-200 leading-relaxed">
  //                 Enterprise-grade encryption 
  //               </p>
  //             </div>

  //             <div className="space-y-2">
  //               <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
  //                 <svg
  //                   className="w-6 h-6"
  //                   fill="currentColor"
  //                   viewBox="0 0 20 20"
  //                 >
  //                   <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
  //                 </svg>
  //               </div>
  //               <h3 className="text-base font-semibold">Analytics</h3>
  //               <p className="text-sm text-blue-200 leading-relaxed">
  //                 Real-time insights
  //               </p>
  //             </div>

  //             <div className="space-y-2">
  //               <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
  //                 <svg
  //                   className="w-6 h-6"
  //                   fill="currentColor"
  //                   viewBox="0 0 20 20"
  //                 >
  //                   <path
  //                     fillRule="evenodd"
  //                     d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
  //                     clipRule="evenodd"
  //                   />
  //                 </svg>
  //               </div>
  //               <h3 className="text-base font-semibold">24/7 Access</h3>
  //               <p className="text-sm text-blue-200 leading-relaxed">
  //                 Available anytime
  //               </p>
  //             </div>

  //             <div className="space-y-2">
  //               <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
  //                 <svg
  //                   className="w-6 h-6"
  //                   fill="currentColor"
  //                   viewBox="0 0 20 20"
  //                 >
  //                   <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
  //                 </svg>
  //               </div>
  //               <h3 className="text-base font-semibold">Team Ready</h3>
  //               <p className="text-sm text-blue-200 leading-relaxed">
  //                 Multi-user support
  //               </p>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     {/* Right Side - Login Form */}
  //     <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-12">
  //       <div className="w-full max-w-md">
  //         {/* Header */}
  //         <div className="mb-10">
  //           <h2 className="text-3xl font-bold text-gray-900 mb-2">
  //             Welcome back
  //           </h2>
  //           <p className="text-sm text-gray-600">
  //             Enter your credentials to access your account
  //           </p>
  //         </div>

  //         {/* Form Card */}
  //         <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
  //           <form className="space-y-6">
  //             <TextInput
  //               name={'email'}
  //               control={control}
  //               rules={{
  //                 required: 'Email is required',
  //                 pattern: {
  //                   value: EMAIL_REGEX,
  //                   message: 'Please enter a valid email',
  //                 },
  //               }}
  //               label={'Email Address'}
  //               labelClassName="text-sm font-semibold text-gray-700"
  //               formState={formState}
  //               config={{
  //                 placeholder: 'you@company.com',
  //                 className: 'h-12 text-base',
  //               }}
  //             />

  //             <TextInput
  //               name={'password'}
  //               control={control}
  //               rules={{
  //                 required: 'Password is required',
  //                 minLength: {
  //                   value: 8,
  //                   message: 'Password must be at least 8 characters',
  //                 },
  //               }}
  //               label={'Password'}
  //               labelClassName="text-sm font-semibold text-gray-700"
  //               variant="password"
  //               formState={formState}
  //               config={{
  //                 placeholder: 'Enter your password',
  //                 className: 'h-12 text-base',
  //               }}
  //             />

  //             <div className="flex items-center justify-end">
  //               <LinkWizard
  //                 href="/forgot-password"
  //                 addOnClass="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
  //               >
  //                 Forgot password?
  //               </LinkWizard>
  //             </div>

  //             <ButtonWizard
  //               onBtnClick={handleSubmit(handleSubmitForm)}
  //               addOnClass="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-md hover:shadow-lg"
  //               loading={isLoading}
  //             >
  //               Sign in
  //             </ButtonWizard>

  //             {/* Divider */}
  //             <div className="relative py-4">
  //               <div className="absolute inset-0 flex items-center">
  //                 <div className="w-full border-t border-gray-300" />
  //               </div>
  //               <div className="relative flex justify-center">
  //                 <span className="px-4 bg-white text-sm text-gray-500 font-medium">
  //                   Or
  //                 </span>
  //               </div>
  //             </div>

  //             {/* OTP Login Button */}
  //             <Link
  //               to="/login-with-otp"
  //               className="w-full h-12 flex items-center justify-center gap-2.5 px-4 border-2 border-gray-300 rounded-xl text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
  //             >
  //               <svg
  //                 className="w-5 h-5"
  //                 fill="none"
  //                 stroke="currentColor"
  //                 viewBox="0 0 24 24"
  //               >
  //                 <path
  //                   strokeLinecap="round"
  //                   strokeLinejoin="round"
  //                   strokeWidth={2}
  //                   d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
  //                 />
  //               </svg>
  //               Sign in with OTP
  //             </Link>
  //           </form>
  //         </div>

  //         {/* Footer */}
  //         <p className="text-center text-xs text-gray-500 mt-8">
  //           &copy; {new Date().getFullYear()}{' '}
  //           <Link
  //             target="_blank"
  //             to="https://sapphiretech.co.in"
  //             className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
  //             rel="noopener noreferrer"
  //           >
  //             Sapphire Technocrats Pvt Ltd.
  //           </Link>
  //         </p>
  //       </div>
  //     </div>
  //   </div>
  // );
// }
